import React, { useEffect } from "react";
import {
  LayoutDashboard,
  ArrowLeft,
  RefreshCw,
  Lock,
  AlertCircle,
  X,
} from "lucide-react";
import AdminAuth, { logout } from "./AdminAuth";
import AdminStatsBar from "./AdminStatsBar";
import AdminQuoteTable from "./AdminQuoteTable";
import AdminDashboardAIAssistant from "./AdminDashboardAIAssistant";
import AdminPricingEditor from "./AdminPricingEditor";
import AdminGoogleReviews from "./AdminGoogleReviews";
import AdminContractEnquiries from "./AdminContractEnquiries";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { useAdminQuotes } from "../../hooks/useAdminQuotes";

const LoadingSpinner = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="text-center space-y-3">
      <RefreshCw className="w-8 h-8 text-sky-600 animate-spin mx-auto" />
      <p className="text-slate-500 font-medium">Loading dashboard...</p>
    </div>
  </div>
);

const AdminHeader = ({ refreshing, onRefresh }) => (
  <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <a
            href="/"
            className="flex items-center gap-2 text-slate-500 hover:text-sky-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Back to Site</span>
          </a>
          <div className="h-6 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5 text-sky-600" />
            <h1 className="text-base sm:text-lg font-bold text-slate-900 whitespace-nowrap">Admin<span className="hidden sm:inline"> Dashboard</span></h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onRefresh}
            aria-label="Refresh"
            data-testid="admin-refresh-btn"
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 transition-colors text-sm font-medium disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={logout}
            aria-label="Logout"
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 border border-slate-200 text-slate-600 rounded-md hover:bg-slate-50 transition-colors text-sm font-medium"
          >
            <Lock className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </div>
  </header>
);

const ErrorBanner = ({ message, onDismiss }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between">
    <div className="flex items-center gap-2 text-red-700 text-sm">
      <AlertCircle className="w-4 h-4 flex-shrink-0" />
      <span>{message}</span>
    </div>
    <button onClick={onDismiss} className="text-red-400 hover:text-red-600 transition-colors">
      <X className="w-4 h-4" />
    </button>
  </div>
);

const AdminDashboard = () => {
  const {
    quotes,
    stats,
    loading,
    refreshing,
    error,
    refresh,
    updateStatus,
    deleteQuote,
    clearError,
  } = useAdminQuotes();

  useEffect(() => {
    window.addEventListener("nw-quotes-refresh", refresh);
    return () => window.removeEventListener("nw-quotes-refresh", refresh);
  }, [refresh]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-slate-50 font-inter">
      <AdminHeader refreshing={refreshing} onRefresh={refresh} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {error && <ErrorBanner message={error} onDismiss={clearError} />}
        <Tabs defaultValue={new URLSearchParams(window.location.search).get("google") ? "reviews" : "quotes"} className="space-y-6">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Quotes</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="admin-tab-pricing">Pricing</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="contracts" data-testid="admin-tab-contracts">Contracts</TabsTrigger>
          </TabsList>
          <TabsContent value="quotes" className="space-y-8">
            <AdminStatsBar stats={stats} />
            <AdminQuoteTable
              quotes={quotes}
              totalCount={quotes.length}
              onStatusChange={updateStatus}
              onDelete={deleteQuote}
            />
          </TabsContent>
          <TabsContent value="pricing">
            <AdminPricingEditor />
          </TabsContent>
          <TabsContent value="reviews">
            <AdminGoogleReviews />
          </TabsContent>
          <TabsContent value="contracts">
            <AdminContractEnquiries />
          </TabsContent>
        </Tabs>
      </main>
      {/* Admin AI Assistant - fixed position, bottom-left */}
      <AdminDashboardAIAssistant stats={stats} recentQuotes={quotes} />
    </div>
  );
};

const AdminWithAuth = () => (
  <AdminAuth>
    <AdminDashboard />
  </AdminAuth>
);

export { AdminWithAuth };
export default AdminDashboard;
